import { defineNuxtPlugin, useRuntimeConfig } from '#imports';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  type SpanExporter,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { useAuth0 } from '@auth0/auth0-vue';
import { useOrganizationValue } from '@/composables/use-organization';
import {
  buildTelemetryResource,
  createOrganizationBaggagePropagator,
  createOtlpAuthHeadersProvider,
  createSilentTokenGetter,
  resolveOtlpIgnoreUrls,
  resolveOtlpTracesEndpoint,
  resolvePropagateTraceHeaderCorsUrls,
  resolveSampler,
  resolveTelemetryExporterKind,
} from '@/utils/telemetry';

/**
 * OTel Web SDK 初期化 (設計 07)。
 *
 * `order: -100` で全 plugin の最先頭に実行し、document-load span を取りこぼさない。
 * fetch/xhr auto-instrumentation が W3C `traceparent` を全 API クライアントへ自動
 * 付与し、独自 `x-trace-id`/`x-span-id` は撤去 (D4)。送信先は Auth0 JWT を検証する
 * 外部 collector (oidcauth-proxy 経由)。
 *
 * `OTEL_COLLECTOR_URL` が無い / 不正なときの挙動は環境で分岐する
 * (resolveTelemetryExporterKind 参照): ローカルのみ ConsoleSpanExporter、それ以外は
 * provider を登録せず計装しない (env 未投入の環境で本番ユーザの console を汚さないため)。
 */
export default defineNuxtPlugin({
  name: 'telemetry',
  order: -100,
  setup(nuxtApp) {
    // telemetry は何があってもアプリ本体を落とさない (fail-open, レビュー指摘 Important)。
    // order:-100 で最先頭に走るため、ここで throw すると後続 plugin (auth0 等) が起動せず
    // アプリ全体が停止する。SDK 初期化失敗時は console.warn 1 回で計装なし続行する。
    try {
      const config = useRuntimeConfig().public;
      const collectorUrl = (config.otelCollectorUrl ?? '').trim();
      const deploymentEnvironment = config.deploymentEnvironment || undefined;
      const exporterKind = resolveTelemetryExporterKind(collectorUrl, deploymentEnvironment);

      // collector URL が「設定されているのに不正 / 非 https」なのは設定ミスなので、環境を問わず
      // 可視化する (exporter 構築 throw による計装全体無効化と、http での Auth0 Bearer 平文
      // 送信を防ぐ意図。Copilot 指摘)。未設定は移行期の想定内なので警告しない。
      if (collectorUrl.length > 0 && exporterKind !== 'otlp') {
        // eslint-disable-next-line no-console -- 設定不備の可視化 (logger plugin は未起動)
        console.warn(
          '[telemetry] OTEL_COLLECTOR_URL が有効な https URL ではありません。OTLP export を行いません。',
        );
      }

      // 非ローカルで collector URL が無い / 不正なら、provider を登録せずここで打ち切る。
      // ConsoleSpanExporter へ倒すと本番ユーザのブラウザ console に全 span が出続けるため
      // (k8s の OTEL 系 env は kubernetes-manifest#3225 で投入予定。それ以前に本 image が
      // stg/prod へ出ても安全にするための分岐)。span 生成も export も行わない。
      if (exporterKind === 'none') {
        return;
      }

      // service.namespace は k8s namespace 群 `datastore-*` から環境サフィックスを落とした値。
      // 環境軸は tenant (`fc-{env}-aks`) と中央 collector が付与する deployment.environment.name で
      // 表現済みのためここには含めない (datastore-api / workflow / activity と同一の値)。
      const resource = buildTelemetryResource({
        serviceName: 'datastore-ui',
        serviceVersion: config.serviceVersion || undefined,
        deploymentEnvironment: config.deploymentEnvironment || undefined,
        serviceNamespace: 'datastore',
      });

      // export 毎に Auth0 の Bearer を遅延取得する (期限切れ / リフレッシュ対応)。
      // - headers callback は BatchSpanProcessor のバックグラウンドタイマ (Nuxt context 外)
      //   から発火するため runWithContext で Nuxt context を張り直す (useAuth0 が throw して
      //   常に degraded になるのを防ぐ)。
      // - アプリの useToken() は refresh token 失効時に navigateTo(LOGOUT) を誘発するため
      //   使わず、getAccessTokenSilently を直接呼ぶ getter を使う (telemetry を認証リダイレクト
      //   に結合させない。失敗時は undefined=degraded export)。
      const getSilentToken = createSilentTokenGetter(() => useAuth0());
      const exporter: SpanExporter =
        exporterKind === 'otlp'
          ? new OTLPTraceExporter({
              url: resolveOtlpTracesEndpoint(collectorUrl),
              headers: createOtlpAuthHeadersProvider({
                getToken: () => Promise.resolve(nuxtApp.runWithContext(getSilentToken)),
              }),
            })
          : new ConsoleSpanExporter();

      const provider = new WebTracerProvider({
        resource,
        sampler: resolveSampler(config.otelTracesSampler, config.otelTracesSamplerArg),
        spanProcessors: [new BatchSpanProcessor(exporter)],
      });

      // organization_id baggage の発生源 (auth0 ready 後に確定する非機密識別子のみ)。
      let currentOrganizationId: string | undefined;
      const baggagePropagator = createOrganizationBaggagePropagator(
        new W3CBaggagePropagator(),
        () => currentOrganizationId,
      );

      provider.register({
        contextManager: new ZoneContextManager(),
        // W3C traceparent + baggage を双方伝播する composite propagator (設計 03 §1)。
        propagator: new CompositePropagator({
          propagators: [new W3CTraceContextPropagator(), baggagePropagator],
        }),
      });

      // cross-origin リクエストへ伝播ヘッダ (traceparent/baggage) を載せる許可先を
      // datastore-api / domain-service / sensyn-id の 3 baseURL に限定する (3rd party 漏洩防止)。
      // ⚠️ propagateTraceHeaderCorsUrls は cross-origin のみを制御し、same-origin リクエストには
      // auto-instrumentation が伝播ヘッダを無条件付与する (same-origin 漏洩の扱いは
      // createOrganizationBaggagePropagator の注記 / PoC-3 を参照)。
      const propagateTraceHeaderCorsUrls = resolvePropagateTraceHeaderCorsUrls([
        config.datastoreApiUrl,
        config.domainServiceApiUrl,
        config.sensynIdApiUrl,
      ]);
      // collector への OTLP POST 自体を計装対象から除外し、export の自己増殖ループを防ぐ
      // (OTLP を使う場合のみ。ConsoleSpanExporter 時は送信が無いため除外不要)。
      const ignoreUrls = resolveOtlpIgnoreUrls(exporterKind === 'otlp' ? collectorUrl : '');

      registerInstrumentations({
        instrumentations: [
          getWebAutoInstrumentations({
            '@opentelemetry/instrumentation-fetch': { propagateTraceHeaderCorsUrls, ignoreUrls },
            '@opentelemetry/instrumentation-xml-http-request': {
              propagateTraceHeaderCorsUrls,
              ignoreUrls,
            },
          }),
        ],
      });

      // auth0 確立後に organization_id を解決し baggage 発生源へ反映する。
      // 組織切替にも追従するよう画面遷移完了時に再解決する。
      const refreshOrganizationId = async () => {
        try {
          currentOrganizationId = await useOrganizationValue();
        } catch {
          currentOrganizationId = undefined;
        }
      };
      nuxtApp.hook('app:mounted', refreshOrganizationId);
      nuxtApp.hook('page:finish', refreshOrganizationId);
    } catch (error) {
      // eslint-disable-next-line no-console -- 初期化失敗を可視化する最後の砦 (logger plugin は未起動)
      console.warn(
        '[telemetry] OpenTelemetry の初期化に失敗しました。計装なしで続行します。',
        error,
      );
    }
  },
});
